import {
  ChangeOption,
  InputOptionInterface,
  SpoIntlOption,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/InputOption.ts';
import { Lang, Localized } from '../../../../../../../../shared/type';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { html, View } from 'rune-ts';
import klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/Input_AddOption.module.scss';
import { INTL_ICONS } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/constants.ts';
import { entries, pipe, map, range, toArray, values, every, each, size } from '@fxts/core';
import {
  cleanCommaString,
  validateDuringInput,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/helpers/input-helper.ts';
import { classes } from '@services/MarppleShop/shared/util';
import { unEscaper } from '@shared/utils/escape.ts';

export class InputOptionIntl
  extends View<SpoIntlOption & { isShowAddButton?: boolean }>
  implements InputOptionInterface
{
  private readonly options: Localized<{ name: Input_TextEditor; value: Input_TextEditor }> = {
    kr: {
      name: new Input_TextEditor({
        name: this.data.name ? formKeyMap.spo_items : formKeyMap.group_name_en,
        value: unEscaper(this.data.name),
        title: '옵션명',
        onInput: this.dispatchChangeOptionEvent.bind(this),
        font_klass: shareFont('14_medium'),
        option: {
          icon: INTL_ICONS.kr,
          placeholder: '예시) 컬러',
        },
      }),
      value: new Input_TextEditor({
        name: this.data._.spo_option_values
          .map(({ name }) => name)
          .filter(Boolean)
          .join(',')
          ? formKeyMap.spo_items
          : formKeyMap.group_name_en,
        value:
          this.data._.spo_option_values
            .map(({ name }) => unEscaper(name))
            .filter(Boolean)
            .join(',') ?? '',
        title: '옵션값',
        onInput: () => {
          const currentValue = this.options.kr.value.getValue();
          const validatedValue = this.validateDuringInput(currentValue);

          // 값이 실제로 변경된 경우에만 setValue 호출
          if (currentValue !== validatedValue) {
            this.options.kr.value.setValue(validatedValue);
          }

          this.dispatchChangeOptionEvent();
        },
        font_klass: shareFont('14_medium'),
        option: {
          placeholder: '예시) 블랙, 블루 등 콤마 ‘,’로 구분',
        },
      }),
    },

    en: {
      name: new Input_TextEditor({
        name: this.data.name_en ? formKeyMap.spo_items : formKeyMap.group_name_en,
        value: unEscaper(this.data.name_en),
        title: '',
        onInput: this.dispatchChangeOptionEvent.bind(this),
        font_klass: shareFont('14_medium'),
        option: {
          icon: INTL_ICONS.en,
          placeholder: '예시) Color',
        },
      }),
      value: new Input_TextEditor({
        name: this.data._.spo_option_values
          .map(({ name_en }) => name_en)
          .filter(Boolean)
          .join(',')
          ? formKeyMap.spo_items
          : formKeyMap.group_name_en,
        value:
          this.data._.spo_option_values
            .map(({ name_en }) => unEscaper(name_en))
            .filter(Boolean)
            .join(',') ?? '',
        title: '',
        onInput: () => {
          const currentValue = this.options.en.value.getValue();
          const validatedValue = this.validateDuringInput(currentValue);

          // 값이 실제로 변경된 경우에만 setValue 호출
          if (currentValue !== validatedValue) {
            this.options.en.value.setValue(validatedValue);
          }

          this.dispatchChangeOptionEvent();
        },
        font_klass: shareFont('14_medium'),
        option: {
          placeholder: '예시) Black, Blue',
        },
      }),
    },

    jp: {
      name: new Input_TextEditor({
        name: this.data.name_jp ? formKeyMap.spo_items : formKeyMap.group_name_en,
        value: unEscaper(this.data.name_jp),
        title: '',
        onInput: this.dispatchChangeOptionEvent.bind(this),
        font_klass: shareFont('14_medium'),
        option: {
          icon: INTL_ICONS.jp,
          placeholder: '예시) カラー',
        },
      }),
      value: new Input_TextEditor({
        name: this.data._.spo_option_values
          .map(({ name_jp }) => name_jp)
          .filter(Boolean)
          .join(',')
          ? formKeyMap.spo_items
          : formKeyMap.group_name_en,
        value:
          this.data._.spo_option_values
            .map(({ name_jp }) => unEscaper(name_jp))
            .filter(Boolean)
            .join(',') ?? '',
        title: '',
        onInput: () => {
          const currentValue = this.options.jp.value.getValue();
          const validatedValue = this.validateDuringInput(currentValue);

          // 값이 실제로 변경된 경우에만 setValue 호출
          if (currentValue !== validatedValue) {
            this.options.jp.value.setValue(validatedValue);
          }

          this.dispatchChangeOptionEvent();
        },
        font_klass: shareFont('14_medium'),
        option: {
          placeholder: '예시) ブルー, レッド',
        },
      }),
    },
  };

  override template() {
    return html`
      <div class="${klass.option}">
        ${pipe(
          entries(this.options),
          map(
            ([lang, option]) => html`
              ${lang !== 'kr'
                ? // 레이아웃을 맞추기 위한 더미 돔 엘리먼트
                  pipe(
                    range(2),
                    map(
                      (position) => html`
                        <div
                          class="${classes(klass.dummy, {
                            [klass.dummy_optional]: position === 0,
                            [klass.hide]: !this.data.isShowAddButton,
                          })}"
                        >
                          <div class="${klass.dummy_child}"></div>
                        </div>
                      `,
                    ),
                    toArray,
                  )
                : ''}
              <div class="${klass.option}">${option.name} ${option.value}</div>
            `,
          ),
          toArray,
        )}
      </div>
    `;
  }

  private getOptionValues() {
    const kr_values = this.options.kr.value.getValue().split(',');
    const en_values = this.options.en.value.getValue().split(',');
    const jp_values = this.options.jp.value.getValue().split(',');

    const max_size = Math.max(size(kr_values), size(en_values), size(jp_values));

    return pipe(
      range(max_size),
      map((index) => ({
        name: kr_values[index] ?? '',
        name_en: en_values[index] ?? '',
        name_jp: jp_values[index] ?? '',
      })),
      toArray,
    );
  }

  dispatchChangeOptionEvent() {
    this.dispatchEvent(ChangeOption, {
      bubbles: true,
    });
  }

  cleanCommaString(input: string): string {
    return cleanCommaString(input);
  }

  validateDuringInput(input: string): string {
    return validateDuringInput(input);
  }

  getOptionInfo(): SpoIntlOption {
    pipe(
      values(this.options),
      each((option) => option.value.setValue(this.cleanCommaString(option.value.getValue()))),
    );

    return {
      name: this.options.kr.name.getValue(),
      name_en: this.options.en.name.getValue(),
      name_jp: this.options.jp.name.getValue(),
      _: {
        spo_option_values: this.getOptionValues(),
      },
    };
  }

  isAllEmpty() {
    return pipe(
      values(this.options),
      every((option) => !option.name.getValue() && !option.value.getValue()),
    );
  }

  isCompleted() {
    const option_values = this.getOptionValues();

    const validateIntlOption = (
      lang: Lang,
      option_value: ReturnType<typeof this.getOptionValues>[number],
    ) => {
      const key = lang === Lang.kr ? ('name' as const) : (`name_${lang}` as const);

      if (!this.options[lang].name.getValue()) {
        this.options[lang].name.setError(true);
        return false;
      }

      this.options[lang].name.setError(false);

      if (!option_value[key]) {
        this.options[lang].value.setError(true);
        return false;
      }

      this.options[lang].value.setError(false);

      return true;
    };

    return pipe(
      option_values,
      every((option_value) => {
        return (
          validateIntlOption(Lang.kr, option_value) &&
          validateIntlOption(Lang.en, option_value) &&
          validateIntlOption(Lang.jp, option_value)
        );
      }),
    );
  }

  removeOptionInfo() {
    this.data.name = '';
    this.data._.spo_option_values = [];
    pipe(
      values(this.options),
      each((option) => {
        option.name.setValue('');
        option.value.setValue('');
      }),
    );
    this.redraw();
    return this;
  }

  setIsShowAddButton(isShowAddButton: boolean) {
    this.data.isShowAddButton = isShowAddButton;
  }

  getViews() {
    return this.options;
  }

  getTextEditors() {
    return this.subViews(Input_TextEditor);
  }
}
